import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { usePreferences } from './PreferencesContext';

export const FONT_SCALES = {
  xs: 0.75,
  sm: 0.875,
  base: 1,
  lg: 1.125,
  xl: 1.25,
  '2xl': 1.5
} as const;

export type FontScale = typeof FONT_SCALES[keyof typeof FONT_SCALES];
export type FontSizeKey = keyof typeof FONT_SCALES;

interface FontSizeContextType {
  fontScale: FontSizeKey;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  resetFontSize: () => void;
}

const FontSizeContext = createContext<FontSizeContextType | undefined>(undefined);

export const FontSizeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { preferences, updatePreference } = usePreferences();

  const getSizeKeys = (): FontSizeKey[] => Object.keys(FONT_SCALES) as FontSizeKey[];

  const increaseFontSize = () => {
    const sizes = getSizeKeys();
    const currentIndex = sizes.indexOf(preferences.fontScale);
    if (currentIndex < sizes.length - 1) {
      const nextSize = sizes[currentIndex + 1];
      updatePreference({ fontScale: nextSize });
    }
  };

  const decreaseFontSize = () => {
    const sizes = getSizeKeys();
    const currentIndex = sizes.indexOf(preferences.fontScale);
    if (currentIndex > 0) {
      const prevSize = sizes[currentIndex - 1];
      updatePreference({ fontScale: prevSize });
    }
  };

  const resetFontSize = () => {
    updatePreference({ fontScale: 'base' });
  };

  useEffect(() => {
    const htmlElement = document.documentElement;
    htmlElement.style.fontSize = `${FONT_SCALES[preferences.fontScale]}rem`;
  }, [preferences.fontScale]);

  return (
    <FontSizeContext.Provider
      value={{
        fontScale: preferences.fontScale,
        increaseFontSize,
        decreaseFontSize,
        resetFontSize,
      }}
    >
      {children}
    </FontSizeContext.Provider>
  );
};

export const useFontSize = () => {
  const context = useContext(FontSizeContext);
  if (!context) {
    throw new Error('useFontSize must be used within a FontSizeProvider');
  }
  return context;
};